import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, List, Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import Swal from 'sweetalert2';
import InstituteCompanyCard from '../../components/institute/Jobs/InstituteCompanyList/InstituteCompanyCard';
import CompanyListItem, { CompanyListHeader } from '../../components/institute/Jobs/InstituteCompanyList/CompanyListItem';
import CompanyListTabs from '../../components/institute/Jobs/InstituteCompanyList/CompanyListTabs';
import CompanySearchFilter from '../../components/institute/Jobs/InstituteCompanyList/CompanySearchFilter';
import CompanyDetailModal from '../../components/institute/Jobs/InstituteCompanyList/CompanyDetailModal';
import { isMobile } from 'react-device-detect';
import PageHeader from '../../components/PageHeader';

const InstituteCompanyList = () => {
  const navigate = useNavigate();

  // Define custom breakpoints for responsive grid
  const breakpoints = {
    0: {
      cardsPerRow: 1,
      className: 'col-12'
    },
    880: {
      cardsPerRow: 2,
      className: 'col-6'
    },
    1080: {
      cardsPerRow: 3,
      className: 'col-4'
    },
    1350: {
      cardsPerRow: 4,
      className: 'col-3'
    }
  };

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingCompanyId, setDeletingCompanyId] = useState(null);
  const [viewMode, setViewMode] = useState(isMobile? 'grid':'list');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterCity, setFilterCity] = useState('all');
  const [activeTab, setActiveTab] = useState('all');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [currentBreakpoint, setCurrentBreakpoint] = useState(breakpoints[0]);
  
  // Pagination states - for frontend pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  const InstituteId = sessionStorage.getItem("instituteId");
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  // Function to get current breakpoint based on window width
  const getCurrentBreakpoint = (width) => {
    const sortedBreakpoints = Object.keys(breakpoints)
      .map(Number)
      .sort((a, b) => b - a);
    
    for (const bp of sortedBreakpoints) {
      if (width >= bp) {
        return breakpoints[bp];
      }
    }
    return breakpoints[0];
  };

  // Handle window resize to update breakpoint
  useEffect(() => {
    const handleResize = () => {
      const newBreakpoint = getCurrentBreakpoint(window.innerWidth);
      setCurrentBreakpoint(newBreakpoint);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // API call function - fetches ALL data without pagination
  const fetchCompanies = async (search = '', city = '', isSearch = false) => {
    try {
      if (isSearch) {
        setSearchLoading(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const params = new URLSearchParams({
        institute_id: parseInt(InstituteId),
        paginate: 0, // Get all data
      });

      if (search) params.append('search', search);
      if (city !== 'all') params.append('city', city);

      const response = await fetch(`${LMS}/api/organisation?${params}`, {
        method: 'GET',
        headers: {
          Authorization: token,
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      console.log(data);

      // Handle both paginated and non-paginated responses
      const companiesData = data.data.data || data.data;

      const transformedCompanies = companiesData.map(company => ({
        id: company.id,
        name: company.organisation_name,
        logo: company.organisation_logo,
        industry: company.industry_domain,
        industryName: company.industry_name,
        city: company.city_name,
        state: company.state_name,
        country: company.country,
        address: `${company.address1}${company.address2 ? ', ' + company.address2 : ''}`,
        contactPerson: company.contact_person_name,
        contactNumber: company.contact_person_number,
        contactEmail: company.contact_person_email,
        size: company.organisation_size,
        about: company.about_us,
        website: company.industry_domain,
        status: company.status === 1 ? 'active' : 'inactive',
        createdAt: company.created_at,
        updatedAt: company.updated_at,
        totalPosts: company.total_jobs || 0,
        totalHired: company.total_hired || 0,
        totalApplications: company.total_application_received || 0,
      }));

      setCompanies(transformedCompanies);

    } catch (err) {
      setError(err.message);
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (InstituteId) {
      fetchCompanies();
    }
  }, [InstituteId]);

  // Handle search with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (InstituteId) {
        setCurrentPage(1); // Reset to page 1 on search
        fetchCompanies(searchTerm, filterCity, true);
      }
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, filterCity, InstituteId]);

  // Reset to page 1 when activeTab or filterIndustry changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, filterIndustry]);

  const navigateToAddCompany = () => navigate('/add-company');
  const navigateToEditCompany = (companyId) => navigate(`/edit-company/${companyId}`);
  const navigateToCompanyJobs = (companyId) => navigate(`/company-jobs/${companyId}`);

  const handleView = (company) => {
    setSelectedCompany(company);
    setShowDetailModal(true);
  };

  const handleEdit = (company) => navigateToEditCompany(company.id);
  
  const handleDelete = async (company) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `Do you want to delete ${company.name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        setDeletingCompanyId(company.id);
        
        Swal.fire({
          title: 'Deleting...',
          text: 'Please wait while we delete the company.',
          icon: 'info',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            Swal.showLoading();
          }
        });
        
        const response = await fetch(`${LMS}/api/organisation`, {
          method: 'DELETE',
          headers: {
            Authorization: token,
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: company.id.toString()
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const apiResult = await response.json();
        
        Swal.fire({
          title: 'Deleted!',
          text: `${company.name} has been deleted successfully.`,
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        
        // Refresh the company list
        fetchCompanies(searchTerm, filterCity);
        
      } catch (err) {
        console.error('Error deleting company:', err);
        
        let errorMessage = 'An unexpected error occurred. Please try again.';
        let errorTitle = 'Error!';
        
        if (err.message.includes('404')) {
          errorMessage = 'Company not found. It may have already been deleted.';
          errorTitle = 'Not Found';
        } else if (err.message.includes('403')) {
          errorMessage = 'You do not have permission to delete this company.';
          errorTitle = 'Access Denied';
        } else if (err.message.includes('500')) {
          errorMessage = 'Server error occurred. Please try again later.';
          errorTitle = 'Server Error';
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        Swal.fire({
          title: errorTitle,
          text: errorMessage,
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      } finally {
        setDeletingCompanyId(null);
      }
    }
  };

  const handleViewJobs = (company) => navigateToCompanyJobs(company.id);

  // Filter companies based on all filters (industry, status, search, city)
  const filteredCompanies = companies.filter(company => {
    const matchesIndustry = filterIndustry === 'all' || company.industry === filterIndustry;
    const matchesStatus = activeTab === 'all' || company.status === activeTab;
    
    return matchesIndustry && matchesStatus;
  });

  // Get unique industries and cities from ALL data
  const industries = [...new Set(companies.map(company => company.industry))];
  const cities = [...new Set(companies.map(company => company.city))];

  // Calculate counts for tabs from ALL companies
  const counts = {
    all: companies.length,
    active: companies.filter(c => c.status === 'active').length,
    inactive: companies.filter(c => c.status === 'inactive').length,
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterIndustry('all');
    setFilterCity('all');
    setActiveTab('all');
    setCurrentPage(1);
    fetchCompanies('', '');
  };

  // Custom grid styles based on current breakpoint
  const getGridStyles = () => ({
    display: 'grid',
    gridTemplateColumns: `repeat(${currentBreakpoint.cardsPerRow}, 1fr)`,
    gap: '1.5rem',
    transition: 'grid-template-columns 0.3s ease'
  });

  // Frontend pagination logic
  const totalFilteredCompanies = filteredCompanies.length;
  const totalPages = Math.ceil(totalFilteredCompanies / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Pagination component
  const PaginationComponent = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="text-muted">
          Showing {startIndex + 1} to {Math.min(endIndex, totalFilteredCompanies)} of {totalFilteredCompanies} companies
        </div>
        
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} />
              </button>
            </li>
            
            {Array.from({ length: totalPages }, (_, index) => {
              const page = index + 1;
              const isActive = page === currentPage;
              
              if (
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 2 && page <= currentPage + 2)
              ) {
                return (
                  <li key={page} className={`page-item ${isActive ? 'active' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  </li>
                );
              } else if (page === currentPage - 3 || page === currentPage + 3) {
                return (
                  <li key={page} className="page-item disabled">
                    <span className="page-link">...</span>
                  </li>
                );
              }
              return null;
            })}
            
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={16} />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Error!</h4>
        <p>{error}</p>
        <button className="btn btn-outline-danger" onClick={() => fetchCompanies()}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="col-12">
        <PageHeader title="Company Listings" 
          breadcrumbs={[
            { label: "Dashboard", path: "/institute-dashboard" },
            { label: "Company Listings", active: true }
          ]}
        />
      </div>
      
      <CompanyListTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        counts={counts}
      />

      <CompanySearchFilter
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterIndustry={filterIndustry}
        setFilterIndustry={setFilterIndustry}
        filterCity={filterCity}
        setFilterCity={setFilterCity}
        industries={industries}
        cities={cities}
        viewMode={viewMode}
        setViewMode={setViewMode}
        navigateToAddCompany={navigateToAddCompany}
        handleClearFilters={handleClearFilters}
        isLoading={searchLoading}
      />

      {searchLoading && (
        <div className="position-relative">
          <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
            <div className="text-center">
              <div className="spinner-border text-primary mb-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="text-muted">Searching...</div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'grid' ? (
        <div style={getGridStyles()}>
          {paginatedCompanies.map(company => (
            <div key={company.id}>
              <InstituteCompanyCard
                company={company}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isDeleting={deletingCompanyId === company.id}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <CompanyListHeader />
            <tbody>
              {paginatedCompanies.map(company => (
                <CompanyListItem
                  key={company.id}
                  company={company}
                  onView={handleView}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  isDeleting={deletingCompanyId === company.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paginatedCompanies.length === 0 && !loading && !searchLoading && (
        <div className="text-center py-5">
          <div className="card border-0 shadow-sm">
            <div className="card-body py-5">
              <h4 className="text-muted mb-2">No companies found</h4>
              <p className="text-muted mb-3">Try adjusting your search or filter criteria, or add a new company.</p>
              <div className="d-flex justify-content-center gap-2">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/add-company')}
                >
                  <Plus size={18} className="me-1" />
                  Add New Company
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PaginationComponent />

      {showDetailModal && (
        <CompanyDetailModal
          company={selectedCompany}
          onClose={() => setShowDetailModal(false)}
          onEdit={handleEdit}
          onViewJobs={handleViewJobs}
        />
      )}
    </div>
  );
};

export default InstituteCompanyList;